import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import LoyaltyItem from '../components/loyalty/LoyaltyItem';
import LoyaltySpecialConditionGroup from '../components/loyalty/LoyaltySpecialConditionGroup';
import LoyaltyCounterBlock from '../components/loyalty/LoyaltyCounterBlock';
import Captcha from '../components/other/Captcha';
import ScrollToTop from '../components/other/ScrollToTop';
import '../css/Loyalty.css';
import { getLoyaltyProducts, getLoyaltySettings } from '../redux/actions/productActions';

// Ova stranica postoji SAMO za vizuelno testiranje - ne dira produkcione komponente niti CSS,
// samo im prosledjuje dummy podatke umesto pravih (socket/REST). Banner i tabela ispod i dalje
// koriste STVARNE redux podatke (isti izgled kao /loyalty-artikli), jer to nije ono sto testiramo.

const BUYERS = ['IMIKOM GROUP', 'TRI O', 'COLOR COMMERCE D.O.O.', 'PC WIZARD', 'STD COMP', 'MOBIL GADGET', 'PINGVIN STUDIO', 'SCADA'];
const PRODUCT_NAMES = [
    'WHIRLPOOL PACF212HP B On/Off prenosni klima uređaj',
    'BEKO BEEPP 120 Inverter klima uređaj',
    'ENERGY SISTEM HyperBeat bluetooth zvučnik',
    'NOBLECHAIRS EPIC TX Gaming stolica',
    'KARCHER K 5 CLASSIC Perač pod pritiskom',
    'XIAOMI 34" G34WQi zakrivljeni Gaming monitor',
    'CHUWI Herobook Pro 14.1" laptop',
    'CHUWI Herobook Plus 15.6" laptop',
];
// Stvarne sifre artikala (iz produkcionog odgovora, 2026-08-25) - da bi se u preview-u ucitale
// PRAVE slike sa resource.ewe.rs (dummy DUMMYxxxxx sifre nemaju sliku na serveru, pa je ostajalo
// prazno mesto umesto slike artikla).
const PRODUCT_CODES = ['ELE02624', 'ELE03239', 'ZVU03220', 'OST07721', 'ALA00380', 'MON03068', 'NOT24384', 'NOT24385'];

const buildItem = (conditionId, conditionLimit, idx, buyer, badges = {}) => ({
    _id: 900000 + conditionId * 100 + idx,
    anCompanyKey: 1789,
    anProductKey: 1000 + idx,
    acProduct: PRODUCT_CODES[idx % PRODUCT_CODES.length],
    acProductName: PRODUCT_NAMES[idx % PRODUCT_NAMES.length],
    acLoyaltyGroup: 'Test grupa',
    anLoyaltyGroupOrdering: idx,
    anPrice: 99.9,
    anOldPrice: 0,
    anQty: 0,
    anQtyVirtual: 1,
    anQtyAvailableForCompany: 1,
    isPriceVisible: 1,
    isSpecialForCompany: badges.isSpecialForCompany ?? 0,
    isSaleConnected: badges.isSaleConnected ?? 0,
    acConditionType: badges.acConditionType ?? 'UslovljenaProdaja',
    anConditionId: conditionId,
    anConditionLimit: conditionLimit,
    anProductId: 9000 + idx,
    anLoyaltyTypeId: 1,
    acBuyer: buyer ?? null,
});

const buildGroup = ({ conditionId, conditionLimit, itemCount = 8, soldIndexes = [], buyerNames = {}, badgeIndexes = {}, isReached, isUsed, canClick, anBuyerTotalPrice = 0 }) => ({
    anConditionId: conditionId,
    anConditionLimit: conditionLimit,
    anBuyerTotalPrice,
    // buyerNames[idx] - override kupca za konkretan indeks (npr. "kupila JA", ne neka druga firma
    // iz BUYERS niza) - koristi se za simulaciju "vec zavrsene" kupovine bez pravog axios poziva.
    // badgeIndexes[idx] - override isSpecialForCompany/isSaleConnected/acConditionType za konkretan
    // indeks - za testiranje bedzeva (special-for-company.png, badges-povezana-prodaja*.png) koji
    // su inace UVEK iskljuceni u dummy podacima.
    specialSlshopList: Array.from({ length: itemCount }, (_, idx) =>
        buildItem(conditionId, conditionLimit, idx, buyerNames[idx] ?? (soldIndexes.includes(idx) ? BUYERS[idx % BUYERS.length] : null), badgeIndexes[idx])
    ),
    isReached,
    isUsed,
    canClick,
    canPurchase: canClick,
});

const SCENARIOS = [
    {
        key: 'loading',
        label: 'Prikaz 0 – Učitavanje (socket još nije stigao)',
        isLive: false,
        isShopActive: 1,
        groups: [
            buildGroup({ conditionId: 101, conditionLimit: 100, isReached: 0, isUsed: 0, canClick: 0, anBuyerTotalPrice: 0 }),
            buildGroup({ conditionId: 102, conditionLimit: 300, isReached: 0, isUsed: 0, canClick: 0, anBuyerTotalPrice: 0 }),
            buildGroup({ conditionId: 103, conditionLimit: 600, itemCount: 1, isReached: 0, isUsed: 0, canClick: 0, anBuyerTotalPrice: 0 }),
        ],
    },
    {
        key: 'shop-inactive',
        label: 'Prikaz 1 – Zaključano (shop van perioda)',
        isLive: true,
        isShopActive: 0,
        groups: [buildGroup({ conditionId: 73, conditionLimit: 150, isReached: 0, isUsed: 0, canClick: 0, anBuyerTotalPrice: 0 })],
    },
    {
        key: 'locked-not-reached',
        label: 'Prikaz 1 – Zaključano (uslov nije dostignut)',
        isLive: true,
        isShopActive: 1,
        groups: [buildGroup({ conditionId: 73, conditionLimit: 150, isReached: 0, isUsed: 0, canClick: 0, anBuyerTotalPrice: 65 })],
    },
    {
        key: 'locked-sold-out',
        label: 'Prikaz 1 – Zaključano (svi artikli već rasprodati)',
        isLive: true,
        isShopActive: 1,
        groups: [buildGroup({ conditionId: 73, conditionLimit: 150, isReached: 0, isUsed: 0, canClick: 0, soldIndexes: [0, 1, 2, 3, 4, 5, 6, 7], anBuyerTotalPrice: 20 })],
    },
    {
        key: 'open',
        label: 'Prikaz 2 – Otvoreno (može da bira)',
        isLive: true,
        isShopActive: 1,
        groups: [buildGroup({ conditionId: 73, conditionLimit: 150, isReached: 1, isUsed: 0, canClick: 1, soldIndexes: [1, 4], anBuyerTotalPrice: 150 })],
    },
    {
        key: 'used',
        label: 'Prikaz 3 – Iskorišćeno (pravo već iskorišćeno)',
        isLive: true,
        isShopActive: 1,
        groups: [buildGroup({ conditionId: 73, conditionLimit: 150, isReached: 1, isUsed: 1, canClick: 0, soldIndexes: [0, 1, 2, 3, 4, 5, 6, 7], anBuyerTotalPrice: 150 })],
    },
    {
        key: 'open-sold-out',
        label: 'Prikaz 3 – Rasprodato u međuvremenu (pravo neiskorišćeno, ali fond prazan)',
        isLive: true,
        isShopActive: 1,
        groups: [buildGroup({ conditionId: 73, conditionLimit: 150, isReached: 1, isUsed: 0, canClick: 1, soldIndexes: [0, 1, 2, 3, 4, 5, 6, 7], anBuyerTotalPrice: 150 })],
    },
    {
        key: 'multi',
        label: 'Više nivoa odjednom (npr. Deal / Super / Premium)',
        isLive: true,
        isShopActive: 1,
        groups: [
            buildGroup({ conditionId: 101, conditionLimit: 100, isReached: 1, isUsed: 0, canClick: 1, soldIndexes: [2], anBuyerTotalPrice: 100 }),
            buildGroup({ conditionId: 102, conditionLimit: 300, isReached: 0, isUsed: 0, canClick: 0, anBuyerTotalPrice: 140 }),
            // Premium je realno najcesce 1 artikal (ne 8 kao Deal/Super) - dummy podatak ovde je
            // namerno sveden na to, ali sama komponenta ne ogranicava broj (itemCount se moze
            // slobodno povecati ako se ikad ponudi vise proizvoda na ovom nivou).
            buildGroup({ conditionId: 103, conditionLimit: 600, itemCount: 1, isReached: 0, isUsed: 0, canClick: 0, soldIndexes: [0], anBuyerTotalPrice: 50 }),
        ],
    },
    {
        key: 'multi-premium-open',
        label: 'Više nivoa odjednom (Premium otključan)',
        isLive: true,
        isShopActive: 1,
        groups: [
            buildGroup({ conditionId: 101, conditionLimit: 100, isReached: 1, isUsed: 0, canClick: 1, soldIndexes: [2], anBuyerTotalPrice: 100 }),
            buildGroup({ conditionId: 102, conditionLimit: 300, isReached: 0, isUsed: 0, canClick: 0, anBuyerTotalPrice: 140 }),
            buildGroup({ conditionId: 103, conditionLimit: 600, itemCount: 1, isReached: 1, isUsed: 0, canClick: 1, anBuyerTotalPrice: 600 }),
        ],
    },
    {
        key: 'multi-premium-bought',
        label: 'Više nivoa odjednom (Premium već kupljen)',
        isLive: true,
        isShopActive: 1,
        groups: [
            buildGroup({ conditionId: 101, conditionLimit: 100, isReached: 1, isUsed: 0, canClick: 1, soldIndexes: [2], anBuyerTotalPrice: 100 }),
            buildGroup({ conditionId: 102, conditionLimit: 300, isReached: 0, isUsed: 0, canClick: 0, anBuyerTotalPrice: 140 }),
            // buyerNames: 0 = "kupila JA" (ne neka druga firma) - prikazuje odmah zavrseno stanje
            // ("Rasprodato"/"Kupio...") bez prolaska kroz pravi Dodaj-u-korpu modal (koji zove pravi backend).
            buildGroup({ conditionId: 103, conditionLimit: 600, itemCount: 1, isReached: 1, isUsed: 1, canClick: 0, buyerNames: { 0: 'Vaša firma (test)' }, anBuyerTotalPrice: 600 }),
        ],
    },
    {
        key: 'multi-special-open',
        label: 'Više nivoa odjednom (Special otključan - može da bira)',
        isLive: true,
        isShopActive: 1,
        groups: [
            buildGroup({ conditionId: 101, conditionLimit: 100, isReached: 0, isUsed: 0, canClick: 0, anBuyerTotalPrice: 40 }),
            buildGroup({ conditionId: 102, conditionLimit: 300, isReached: 1, isUsed: 0, canClick: 1, soldIndexes: [1, 4], anBuyerTotalPrice: 300 }),
            buildGroup({ conditionId: 103, conditionLimit: 600, itemCount: 1, isReached: 0, isUsed: 0, canClick: 0, anBuyerTotalPrice: 50 }),
        ],
    },
    {
        key: 'no-premium',
        label: 'Bez Premium nivoa (samo Deal + Super)',
        isLive: true,
        isShopActive: 1,
        groups: [
            buildGroup({ conditionId: 101, conditionLimit: 100, isReached: 1, isUsed: 0, canClick: 1, soldIndexes: [2], anBuyerTotalPrice: 100 }),
            buildGroup({ conditionId: 102, conditionLimit: 300, isReached: 0, isUsed: 0, canClick: 0, anBuyerTotalPrice: 140 }),
        ],
    },
    {
        key: 'deal5-super4-premium1',
        label: 'Deal 3 / Super 4 / Premium 1 artikal',
        isLive: true,
        isShopActive: 1,
        groups: [
            buildGroup({ conditionId: 101, conditionLimit: 100, itemCount: 3, isReached: 1, isUsed: 0, canClick: 1, soldIndexes: [1], anBuyerTotalPrice: 100 }),
            buildGroup({ conditionId: 102, conditionLimit: 300, itemCount: 4, isReached: 1, isUsed: 0, canClick: 1, soldIndexes: [2], anBuyerTotalPrice: 300 }),
            buildGroup({ conditionId: 103, conditionLimit: 600, itemCount: 1, isReached: 1, isUsed: 0, canClick: 1, anBuyerTotalPrice: 600 }),
        ],
    },
    {
        key: 'all-used-sold-out',
        label: 'Sve rasprodato (Deal iskorišćeno, Super neiskorišćeno, Premium nedostignuto)',
        isLive: true,
        isShopActive: 1,
        groups: [
            // Deal - dostignuto, pravo VEC ISKORISCENO (isUsed:1, canClick:0), sve rasprodato.
            buildGroup({ conditionId: 101, conditionLimit: 100, isReached: 1, isUsed: 1, canClick: 0, soldIndexes: [0, 1, 2, 3, 4, 5, 6, 7], anBuyerTotalPrice: 100 }),
            // Super - dostignuto, pravo JOS NEISKORISCENO (canClick:1), ali je ceo fond vec
            // rasprodat drugima pre nego sto je ovaj korisnik stigao da bira.
            buildGroup({ conditionId: 102, conditionLimit: 300, isReached: 1, isUsed: 0, canClick: 1, soldIndexes: [0, 1, 2, 3, 4, 5, 6, 7], anBuyerTotalPrice: 300 }),
            // Premium - NIJE dostignut limit (isReached:0, Prikaz 1 zakljucano), a jedini artikal je
            // takodje vec prodat (za poruku "svi artikli vec iskorisceni" umesto "jos X EUR").
            buildGroup({ conditionId: 103, conditionLimit: 600, itemCount: 1, isReached: 0, isUsed: 0, canClick: 0, soldIndexes: [0], anBuyerTotalPrice: 400 }),
        ],
    },
    {
        key: 'used-not-all-sold',
        label: 'Iskorišćeno pravo, ali NIJE sve rasprodato',
        isLive: true,
        isShopActive: 1,
        // canClick:0 (pravo vec iskorisceno), ali soldIndexes pokriva SAMO deo artikala - fond jos
        // uvek ima neprodatih artikala za druge firme, za razliku od "used" scenarija gde je sve prodato.
        groups: [buildGroup({ conditionId: 73, conditionLimit: 150, isReached: 1, isUsed: 1, canClick: 0, soldIndexes: [0, 2, 5], anBuyerTotalPrice: 150 })],
    },
    {
        key: 'own-purchase-deal-super',
        label: 'Vaša firma kupila (Deal + Super)',
        isLive: true,
        isShopActive: 1,
        groups: [
            // buyerNames: 0 = "kupila JA" - simulira da je BAS ova kompanija (ne neka druga firma)
            // vec zavrsila kupovinu, isto kao ranije samo za Premium (multi-premium-bought).
            buildGroup({ conditionId: 101, conditionLimit: 100, isReached: 1, isUsed: 1, canClick: 0, buyerNames: { 0: 'Vaša firma (test)' }, anBuyerTotalPrice: 100 }),
            buildGroup({ conditionId: 102, conditionLimit: 300, isReached: 1, isUsed: 1, canClick: 0, buyerNames: { 0: 'Vaša firma (test)' }, anBuyerTotalPrice: 300 }),
        ],
    },
    {
        key: 'deal-2-items',
        label: 'Deal sa samo 2 artikla (granični slučaj centriranja)',
        isLive: true,
        isShopActive: 1,
        groups: [buildGroup({ conditionId: 101, conditionLimit: 100, itemCount: 2, isReached: 1, isUsed: 0, canClick: 1, soldIndexes: [0], anBuyerTotalPrice: 100 })],
    },
];

const LoyaltyJokerPreview = () => {
    const dispatch = useDispatch();
    const loyaltyProductsList = useSelector((state) => state.getLoyaltyProducts);
    const loyaltySettings = useSelector((state) => state.getLoyaltySettings);
    const getUserInfo = useSelector((state) => state.userInfo.userInfo.companyAdditionalInfo);
    const { t } = useTranslation('loyaltyShopPage');
    const now = Date.now();
    const adLoyaltyStartDate = Date.parse(loyaltySettings.adLoyaltyStartDate);
    const adLoyaltyEndDate = Date.parse(loyaltySettings.adLoyaltyEndDate);

    const [activeShop, setActiveShop] = useState(0);
    const [finishCart, setFinishCart] = useState(false);
    const [emptyValues, setEmptyValues] = useState([]);
    const [show, setShow] = useState(false);
    const [hideModal, setHideModal] = useState(false);
    const [shouldAnimate, setShouldAnimate] = useState(false);
    const [isCancelButtonClicked, setIsCancelButtonClicked] = useState(null);

    const [scenarioKey, setScenarioKey] = useState(SCENARIOS[0].key);
    const scenario = SCENARIOS.find((s) => s.key === scenarioKey) || SCENARIOS[0];

    const [clickedSpecialItems, setClickedSpecialItems] = useState({});
    const [canceledItems, setCanceledItems] = useState({});
    const [confirmedPurchases, setConfirmedPurchases] = useState({});
    // Simulira da backend obori canClick na 0 NA SAM KLIK (pre Dodaj-u-korpu potvrde) - u pravom
    // toku se to desava preko socket-a, ovde nemamo pravi backend pa ga simuliramo lokalno. Bez
    // ovoga, posle Odustani, dummy scenario.canClick ostaje NEPROMENJEN (staticki podatak), pa se
    // Condition vraca na "otvoreno, izaberi" umesto na "iskorisceno pravo" - Odustani NE vraca
    // pravo, samo uklanja artikal iz korpe (pravo je vec potroseno na sam klik).
    const [usedConditionIds, setUsedConditionIds] = useState({});

    // Promena scenarija u dropdownu resetuje lokalno stanje klikova - da se otvorena/potvrdjena
    // kartica ne "prenese" iz jednog test-scenarija u drugi.
    useEffect(() => {
        setClickedSpecialItems({});
        setCanceledItems({});
        setConfirmedPurchases({});
        setIsCancelButtonClicked(null);
        setUsedConditionIds({});
    }, [scenarioKey]);

    // Grupe sa lokalno "obrenim" canClick-om (vidi usedConditionIds gore) - koristi se svuda
    // umesto scenario.groups direktno, da Odustani stvarno pokaze "iskorisceno pravo".
    const groups = scenario.groups.map((g) => (
        usedConditionIds[g.anConditionId] ? { ...g, canClick: 0 } : g
    ));

    useEffect(() => {
        dispatch(getLoyaltyProducts(1));
        dispatch(getLoyaltySettings(1));
    }, [dispatch]);

    const setActiveShopFunction = (val) => setActiveShop(val);

    // Simulira odgovor servera na klik (bez pravog axios poziva) - isto ponasanje kao pravi
    // handleItemClick, da se moze testirati flip/"Cestitamo"/dugme za korpu tok na dummy podacima.
    const handleItemClick = (itemId, anLoyaltyTypeId, anConditionId, key) => {
        setClickedSpecialItems((prev) => ({ ...prev, [key]: true }));
        setUsedConditionIds((prev) => ({ ...prev, [anConditionId]: true }));
        setShouldAnimate(true);
        setIsCancelButtonClicked(null);
        setTimeout(() => {
            // acBuyer se NE postavlja ovde - u pravom toku ostaje null sve dok korisnik ne zavrsi
            // kroz "Dodaj u korpu" (i socket ne potvrdi). Da smo ga postavili, LoyaltySpecialItem
            // bi mislio da je artikal vec nekome prodat i prikazao "Rasprodato"/"Kupio" umesto
            // Odustani/Dodaj-u-korpu dugmadi.
            const group = scenario.groups.find((g) => g.anConditionId === anConditionId);
            const item = group?.specialSlshopList.find((i) => i._id === itemId);
            setConfirmedPurchases((prev) => ({ ...prev, [key]: item }));
        }, 400);
    };

    const handleCancelButtonClick = (key) => {
        setIsCancelButtonClicked(key);
        setConfirmedPurchases((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
        });
        setCanceledItems((prev) => ({ ...prev, [key]: true }));
        setTimeout(() => {
            setShouldAnimate(false);
            setIsCancelButtonClicked(null);
        }, 2000);
    };

    const handleClose = () => setShow(false);
    const handleSubmit = (event) => {
        event.preventDefault();
        setShow(true);
    };

    const renderButtonSubmitLoyalty = () => {
        if (getUserInfo?.acCompanyLoyaltyStatus && (getUserInfo.acCompanyLoyaltyStatus.trim().toUpperCase() != 'CRVEN' && loyaltySettings.anCompanyTarget < loyaltySettings.anCompanyTargetAchieved && activeShop == 1 && (adLoyaltyStartDate <= now && adLoyaltyEndDate >= now))) {
            return (
                <div className={`${emptyValues.length == 0 || emptyValues.filter(i => i.value == 0).length > 0 ? "button-submit-ly-shop-disabled" : ""} button-submit-ly-shop`}>
                    <button type="submit" className="btn btn-success" disabled={emptyValues.length == 0 || emptyValues.filter(i => i.value == 0).length > 0 ? true : false} onClick={(e) => handleSubmit(e)} title={emptyValues.length == 0 || emptyValues.filter(i => i.value == 0).length > 0 ? t('validacija') : ""}><i className="fa fa-shopping-cart" aria-hidden="true"></i>&nbsp; {t('dugme')}</button>
                </div>
            );
        }
    };

    // Ista logika kao na pravoj /loyalty-artikli stranici - Super/Premium dele gornji red (2/3+1/3),
    // Deal (i svaki naredni) ide u svoj pun red ispod. colorIndex ostaje STVARNA pozicija u nizu.
    const renderGridBlock = (condition, idx) => (
        <LoyaltySpecialConditionGroup
            key={condition.anConditionId}
            condition={condition}
            colorIndex={idx}
            handleCancelButtonClick={handleCancelButtonClick}
            clickedSpecialItems={clickedSpecialItems}
            canceledItems={canceledItems}
            isCancelButtonClicked={isCancelButtonClicked}
            setShouldAnimate={setShouldAnimate}
            finishCart={finishCart}
            shopIsActive={activeShop}
            isShopActive={scenario.isShopActive}
            isLive={scenario.isLive}
            getUserInfo={getUserInfo}
            loyaltySettings={loyaltySettings}
            confirmedPurchases={confirmedPurchases}
            setEmptyValues={setEmptyValues}
            emptyValues={emptyValues}
            setFinishCartFunction={setFinishCart}
            onItemClick={handleItemClick}
            mode="grid"
        />
    );

    return (
        <>
            {loyaltySettings.length != 0 && (
                <div className="container-content container-main-content target-content loyalty-shop-content-container">
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-sm-12">
                                <LoyaltyCounterBlock loyaltySettings={loyaltySettings} setActiveShopFunction={setActiveShopFunction} activeShop={activeShop} />
                            </div>

                            {loyaltyProductsList.isSaleConnected == 1 && (
                                <div className="col-lg-12">
                                    <div className="info-icons-action">
                                        <span>{t('paragraf7')}</span> <img alt={t('bedz')} className="smallBadge" src="/images/loyalty/badges-povezana-prodaja.png" /> <span>{t('paragraf8')}</span> <img alt={t('bedz')} className="smallBadge" src="/images/loyalty/badges-povezana-prodaja-artikal.png" /> <span>{t('paragraf9')}</span>
                                    </div>
                                </div>
                            )}
                            {getUserInfo?.acCompanyLoyaltyStatus && getUserInfo.acCompanyLoyaltyStatus.trim().toUpperCase() == 'CRVEN' &&
                                <div className="col-sm-12">
                                    <div className="alert alert-danger w-100 alert-info-loyalty" role="alert">
                                        {t('paragraf10')}
                                    </div>
                                </div>
                            }

                            {/* Isti raspored kao na pravoj /loyalty-artikli stranici - summary red iznad,
                                pa "?" kartice ispod (vidi LoyaltyShopPage.js za identicnu strukturu). */}
                            <div className="col-sm-12">
                                <div className="joker-summary-row">
                                    {groups.map((condition, idx) => (
                                        <LoyaltySpecialConditionGroup
                                            key={condition.anConditionId}
                                            condition={condition}
                                            colorIndex={idx}
                                            handleCancelButtonClick={handleCancelButtonClick}
                                            clickedSpecialItems={clickedSpecialItems}
                                            canceledItems={canceledItems}
                                            isCancelButtonClicked={isCancelButtonClicked}
                                            setShouldAnimate={setShouldAnimate}
                                            finishCart={finishCart}
                                            shopIsActive={activeShop}
                                            isShopActive={scenario.isShopActive}
                                            isLive={scenario.isLive}
                                            getUserInfo={getUserInfo}
                                            loyaltySettings={loyaltySettings}
                                            confirmedPurchases={confirmedPurchases}
                                            setEmptyValues={setEmptyValues}
                                            emptyValues={emptyValues}
                                            setFinishCartFunction={setFinishCart}
                                            onItemClick={handleItemClick}
                                            mode="summary"
                                        />
                                    ))}
                                </div>
                            </div>

                            {(() => {
                                const dealCond = groups[0];
                                const superCond = groups[1];
                                const premiumCond = groups[2];
                                const restConds = groups.slice(3);
                                return (
                                    <>
                                        {premiumCond ? (
                                            <div className="col-sm-12">
                                                <div className="joker-grid-top-row">
                                                    {superCond && <div className="joker-grid-col-wide">{renderGridBlock(superCond, 1)}</div>}
                                                    <div className="joker-grid-col-narrow">{renderGridBlock(premiumCond, 2)}</div>
                                                </div>
                                            </div>
                                        ) : (
                                            // Nema Premium-a - Super se siri na CEO red (isto sto i Deal), ne
                                            // ostaje zaglavljen na 2/3 sirine sa praznom kolonom pored sebe.
                                            superCond && renderGridBlock(superCond, 1)
                                        )}
                                        {dealCond && renderGridBlock(dealCond, 0)}
                                        {restConds.map((condition, i) => renderGridBlock(condition, i + 3))}
                                    </>
                                );
                            })()}

                            <div className="col-sm-12">
                                <div className="loyalty-table-content">
                                    <form id="form-loyalty-shop">
                                        <table className="table table-loyalty">
                                            <thead>
                                                <tr>
                                                    <th className="text-align-center" style={{ width: '100px' }}>{t('kolona1')}</th>
                                                    <th>{t('kolona2')}</th>
                                                    <th>{t('kolona3')}</th>
                                                    <th>{t('kolona4')}</th>
                                                    <th className="text-align-right">{t('kolona5')}</th>
                                                    <th className="text-align-right">{t('kolona6')}</th>
                                                    <th className="text-align-right">{t('kolona7')}</th>
                                                    <th className="text-align-right">{t('kolona8')}</th>
                                                    <th>{t('kolona9')}</th>
                                                    <th style={{ width: '100px' }}>{t('kolona10')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {loyaltyProductsList.companyLoyalityShop && loyaltyProductsList.companyLoyalityShop.map((item, key) => (
                                                    item.loyaltyShopItemList.length !== 0 ? (
                                                        <React.Fragment key={key}>
                                                            <tr className="custom-name-title">
                                                                <td colSpan={10}>
                                                                    {item.acLoyaltyGroup === '' ? (
                                                                        <div className="loyalty-header-info-products">{t('paragraf11')}</div>
                                                                    ) : (
                                                                        <div className="loyalty-header-info-products">{item.acLoyaltyGroup}</div>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                            {item.loyaltyShopItemList.map((item2, key2) => (
                                                                <LoyaltyItem finishCart={finishCart} key={key2} keyItem={key2} shopIsActive={activeShop} getUserInfo={getUserInfo} loyaltySettings={loyaltySettings} item={item2} setEmptyValues={setEmptyValues} emptyValues={emptyValues} />
                                                            ))}
                                                        </React.Fragment>
                                                    ) : null
                                                ))}
                                            </tbody>
                                        </table>
                                    </form>
                                    {renderButtonSubmitLoyalty()}
                                </div>
                            </div>

                            {/* Kontrola SAMO za ovu probnu stranicu - bira dummy scenario za "uslove"
                                (Condition nivoe) iznad. Ne postoji na pravoj /loyalty-artikli stranici. */}
                            <div className="col-sm-12" style={{ margin: '40px 0', padding: '20px', border: '2px dashed #999', borderRadius: '8px' }}>
                                <label htmlFor="scenario-select" style={{ fontWeight: 'bold', marginRight: '12px' }}>
                                    Test scenario (uslovi):
                                </label>
                                <select
                                    id="scenario-select"
                                    value={scenarioKey}
                                    onChange={(e) => setScenarioKey(e.target.value)}
                                    style={{ padding: '8px 12px', minWidth: '420px' }}
                                >
                                    {SCENARIOS.map((s) => (
                                        <option key={s.key} value={s.key}>{s.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <ScrollToTop />
            <Modal
                show={show}
                onHide={handleClose}
                keyboard={false}
                dialogClassName={`modal-xl ${hideModal ? 'closeLoyModal' : 'closeLoyModalHidden'}`}
                aria-labelledby=""
                centered
            >
                <div className="close-action-modal" variant="secondary" onClick={handleClose}>
                    <i className="far fa-times-circle"></i>
                </div>
                <Modal.Header>
                    <h4>{t('naslov2')}</h4>
                </Modal.Header>
                <Modal.Body>
                    <Captcha setHideModal={setHideModal} setFinishCartFunction={setFinishCart} finishCart={finishCart} handleClose={handleClose} setEmptyValues={setEmptyValues} type={1} start={loyaltySettings.adLoyaltyStartDate} end={loyaltySettings.adLoyaltyEndDate} socketResponse={{}} />
                </Modal.Body>
            </Modal>
        </>
    );
};

export default LoyaltyJokerPreview;
